import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import EmptyState from '../components/EmptyState';
import { AssetMultiPicker } from '../components/AssetPicker';
import {
  listPortfolios, createPortfolio, deletePortfolio, getPortfolioStats,
  listFeesPresets,
  PORTFOLIO_TYPES,
  formatCurrency, formatDate,
} from '../lib/api';
import { Plus, Briefcase, Trash2, TrendingUp, TrendingDown, Minus, ChevronLeft, ChevronRight, Check } from 'lucide-react';

const TYPE_BADGE = {
  synthetic_from_scratch: { label: 'Sintetico', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  synthetic_live: { label: 'Sintetico live', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  real_reconstructed: { label: 'Reale ricostruito', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
};

const PortfoliosPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [portfolios, setPortfolios] = useState([]);
  const [statsMap, setStatsMap] = useState({});  // {portfolio_id: stats}
  const [presets, setPresets] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);

  // Form (3 step)
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('synthetic_from_scratch');
  const [formBudget, setFormBudget] = useState('10000');
  const [formAssets, setFormAssets] = useState(['SPY']);  // array di ticker
  const [formStrategy, setFormStrategy] = useState('');
  const [formPresetId, setFormPresetId] = useState('no_fees');
  const [formNotes, setFormNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [portfoliosRes, presetsRes] = await Promise.all([
        listPortfolios(),
        listFeesPresets(),
      ]);
      const items = portfoliosRes.data;
      setPortfolios(items);
      setPresets(presetsRes.data);

      // Stats in parallel — non blocca il render della lista
      const statsResults = await Promise.allSettled(
        items.map((p) => getPortfolioStats(p.id))
      );
      const map = {};
      items.forEach((p, idx) => {
        if (statsResults[idx].status === 'fulfilled') {
          map[p.id] = statsResults[idx].value.data;
        }
      });
      setStatsMap(map);
    } catch (e) {
      toast.error('Errore nel caricamento portafogli');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreateDialog = useCallback((prefill = {}) => {
    setFormName(prefill.name || '');
    setFormType(prefill.type || 'synthetic_from_scratch');
    setFormBudget(prefill.budget || '10000');
    setFormAssets(prefill.assets || ['SPY']);
    setFormStrategy(prefill.strategy || '');
    setFormPresetId(prefill.presetId || 'no_fees');
    setFormNotes(prefill.notes || '');
    setWizardStep(1);
    setDialogOpen(true);
  }, []);

  // Cross-link: ?new=1 apre il wizard; ?asset=SPY o ?strategy=donchian
  // pre-popolano lo step 2. I param vengono consumati e rimossi.
  useEffect(() => {
    if (loading) return;
    const isNew = searchParams.get('new') === '1';
    const asset = searchParams.get('asset');
    const strategy = searchParams.get('strategy');
    if (isNew || asset || strategy) {
      openCreateDialog({
        assets: asset ? [asset.toUpperCase()] : ['SPY'],
        strategy: strategy || '',
      });
      // Consuma i param per non riaprire il wizard a ogni refresh
      const sp = new URLSearchParams(searchParams);
      ['new', 'asset', 'strategy'].forEach((k) => sp.delete(k));
      setSearchParams(sp, { replace: true });
    }
  }, [loading, searchParams, setSearchParams, openCreateDialog]);

  const canAdvanceStep1 = formName.trim().length > 0 && parseFloat(formBudget) > 0;
  const handleNext = () => {
    if (wizardStep === 1 && !canAdvanceStep1) {
      toast.error('Inserisci nome e budget valido');
      return;
    }
    setWizardStep((s) => Math.min(3, s + 1));
  };
  const handleBack = () => setWizardStep((s) => Math.max(1, s - 1));

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!formName.trim() || !formBudget) {
      toast.error('Nome e budget sono obbligatori');
      return;
    }
    const budget = parseFloat(formBudget);
    if (!(budget > 0)) {
      toast.error('Budget deve essere maggiore di 0');
      return;
    }
    const assets = (formAssets || []).map((s) => s.toUpperCase()).filter(Boolean);

    const preset = presets.find((p) => p.id === formPresetId);
    const fees_profile_inline = preset
      ? { fixed: preset.fixed, pct: preset.pct, min: preset.min, max: preset.max }
      : null;

    const payload = {
      name: formName.trim(),
      type: formType,
      budget_initial: budget,
      asset_coverage: assets,
      strategy_name: formStrategy.trim() || null,
      fees_profile_inline,
      notes: formNotes.trim() || null,
    };

    setSubmitting(true);
    try {
      await createPortfolio(payload);
      toast.success('Portafoglio creato');
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Errore nella creazione');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Eliminare "${p.name}"?\nTutti i trade associati saranno persi.`)) return;
    try {
      await deletePortfolio(p.id);
      toast.success('Portafoglio eliminato');
      await loadData();
    } catch (e) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const renderPnL = (stats) => {
    // Sprint 6.A.7: usa pnl_total (realized + unrealized) e pnl_pct_on_cost
    const pnl = stats?.pnl_total ?? 0;
    const pct = stats?.pnl_pct_on_cost;
    if (!stats || stats.n_trades === 0) {
      return (
        <div className="flex items-center gap-1 text-muted-foreground text-sm">
          <Minus className="w-4 h-4" />
          <span>Nessun trade</span>
        </div>
      );
    }
    const sign = pnl >= 0 ? '+' : '';
    const cls = pnl > 0
      ? 'text-emerald-600 dark:text-emerald-400'
      : pnl < 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground';
    const Icon = pnl > 0 ? TrendingUp : pnl < 0 ? TrendingDown : Minus;
    return (
      <div className={`flex items-center gap-1 ${cls} text-sm font-medium`}>
        <Icon className="w-4 h-4" />
        <span>{sign}{formatCurrency(pnl)}</span>
        {pct != null && (
          <span className="text-xs font-normal opacity-80">({sign}{pct.toFixed(2)}%)</span>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-primary" />
            I miei portafogli
          </h1>
          <p className="text-muted-foreground text-sm">
            Workbench di simulazione su portafogli personali. Niente broker esterni — tutto interno.
          </p>
        </div>
        <Button onClick={openCreateDialog} data-testid="portfolio-new-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuovo portafoglio
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Caricamento…</div>
      ) : portfolios.length === 0 ? (
        <EmptyState
          icon={Briefcase}
          title="Nessun portafoglio ancora"
          description="Crea il tuo primo portafoglio per iniziare a simulare strategie sui tuoi asset preferiti, oppure ricostruisci il tuo portafoglio reale per tracciarne il PnL."
          action={{ onClick: openCreateDialog, label: 'Crea il primo', icon: Plus }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {portfolios.map((p) => {
            const stats = statsMap[p.id];
            const badge = TYPE_BADGE[p.type] || TYPE_BADGE.synthetic_from_scratch;
            return (
              <Card
                key={p.id}
                className="flex flex-col cursor-pointer hover:shadow-md transition-shadow"
                data-testid={`portfolio-card-${p.id}`}
                onClick={() => navigate(`/portfolios/${p.id}`)}
              >
                <CardHeader className="space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-lg leading-tight">{p.name}</CardTitle>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive shrink-0"
                      onClick={(e) => { e.stopPropagation(); handleDelete(p); }}
                      aria-label="Elimina portafoglio"
                      data-testid={`portfolio-delete-${p.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded ${badge.color}`}>
                      {badge.label}
                    </span>
                    {p.strategy_name && (
                      <Badge variant="outline" className="text-xs">{p.strategy_name}</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="flex-1 space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-xs text-muted-foreground">Liquidità</div>
                      <div className="font-medium">{formatCurrency(p.budget_initial)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Patrimonio</div>
                      <div className="font-medium">
                        {formatCurrency(stats?.patrimony_total ?? stats?.equity_current ?? p.budget_initial)}
                      </div>
                    </div>
                  </div>
                  {stats?.cost_basis_open > 0 && (
                    <div className="text-xs text-muted-foreground">
                      Capitale investito: <span className="text-foreground font-medium">{formatCurrency(stats.cost_basis_open)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between border-t pt-3">
                    <div>
                      <div className="text-xs text-muted-foreground">PnL netto</div>
                      {renderPnL(stats)}
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Trade</div>
                      <div className="font-medium">
                        {stats?.n_trades ?? 0}
                        {stats?.n_open ? <span className="text-xs text-muted-foreground"> ({stats.n_open} open)</span> : null}
                      </div>
                    </div>
                  </div>
                  {p.asset_coverage?.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap">
                      {p.asset_coverage.slice(0, 6).map((a) => (
                        <Badge key={a} variant="secondary" className="text-[10px]">{a}</Badge>
                      ))}
                      {p.asset_coverage.length > 6 && (
                        <span className="text-xs text-muted-foreground">+{p.asset_coverage.length - 6}</span>
                      )}
                    </div>
                  )}
                  {stats?.last_trade_at && (
                    <div className="text-xs text-muted-foreground">
                      Ultimo trade: {formatDate(stats.last_trade_at)}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo portafoglio · Step {wizardStep}/3</DialogTitle>
            <DialogDescription>
              {wizardStep === 1 && 'Tipo, nome e budget iniziale. Niente broker esterni — tutto interno.'}
              {wizardStep === 2 && 'Asset coperti e strategia (opzionale).'}
              {wizardStep === 3 && 'Profilo commissioni — preset broker IT o senza fee.'}
            </DialogDescription>
          </DialogHeader>

          {/* Stepper */}
          <div className="flex items-center gap-2 mb-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2 flex-1">
                <div className={`
                  w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
                  ${s < wizardStep ? 'bg-primary text-primary-foreground' :
                    s === wizardStep ? 'bg-primary/20 text-primary border-2 border-primary' :
                    'bg-muted text-muted-foreground'}
                `}>
                  {s < wizardStep ? <Check className="w-3 h-3" /> : s}
                </div>
                {s < 3 && (
                  <div className={`flex-1 h-0.5 ${s < wizardStep ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </div>
            ))}
          </div>

          {wizardStep === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="p-type">Tipo di portafoglio</Label>
                <Select value={formType} onValueChange={setFormType}>
                  <SelectTrigger id="p-type" data-testid="portfolio-form-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PORTFOLIO_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        <div>
                          <div className="font-medium">{t.label}</div>
                          <div className="text-xs text-muted-foreground">{t.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="p-name">Nome</Label>
                <Input
                  id="p-name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="es. Mio Fineco ETF"
                  autoFocus required
                  data-testid="portfolio-form-name"
                />
              </div>
              <div>
                <Label htmlFor="p-budget">Budget iniziale (€)</Label>
                <Input
                  id="p-budget" type="number" min="1" step="100"
                  value={formBudget} onChange={(e) => setFormBudget(e.target.value)} required
                />
              </div>
            </div>
          )}

          {wizardStep === 2 && (
            <div className="space-y-4">
              <div>
                <Label>Asset coperti</Label>
                <AssetMultiPicker
                  value={formAssets}
                  onChange={setFormAssets}
                  dataTestId="portfolio-asset-picker"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Cerca per nome o ticker. Mantieni vuoto per portafogli reali (li popoli inserendo le operazioni passate).
                </p>
              </div>
              <div>
                <Label htmlFor="p-strategy">Strategia (opzionale)</Label>
                <Input
                  id="p-strategy"
                  value={formStrategy}
                  onChange={(e) => setFormStrategy(e.target.value)}
                  placeholder="es. donchian_breakout_20"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Necessaria per i portafogli "sintetico live" (l'auto-trader la esegue automaticamente).
                </p>
              </div>
              <div>
                <Label htmlFor="p-notes">Note</Label>
                <Textarea
                  id="p-notes"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  rows={2}
                  placeholder="es. Portafoglio Fineco ricostruito da gennaio 2024"
                />
              </div>
            </div>
          )}

          {wizardStep === 3 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="p-fees">Profilo commissioni</Label>
                <Select value={formPresetId} onValueChange={setFormPresetId}>
                  <SelectTrigger id="p-fees" data-testid="portfolio-form-fees">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {presets.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(() => {
                const sel = presets.find((p) => p.id === formPresetId);
                return sel ? (
                  <Card>
                    <CardContent className="pt-4 space-y-1 text-xs">
                      <div className="font-medium text-sm">{sel.label}</div>
                      {sel.note && <p className="text-muted-foreground">{sel.note}</p>}
                      <div className="grid grid-cols-4 gap-2 pt-2 text-muted-foreground">
                        <div>Fisso: <span className="font-medium text-foreground">{sel.fixed}€</span></div>
                        <div>%: <span className="font-medium text-foreground">{sel.pct}%</span></div>
                        <div>Min: <span className="font-medium text-foreground">{sel.min}€</span></div>
                        <div>Max: <span className="font-medium text-foreground">{sel.max === 0 ? '—' : sel.max + '€'}</span></div>
                      </div>
                    </CardContent>
                  </Card>
                ) : null;
              })()}
              <Card className="bg-muted/50">
                <CardContent className="pt-4 text-sm space-y-1">
                  <div className="font-medium">Riepilogo</div>
                  <div className="text-xs text-muted-foreground">
                    <div>Tipo: <span className="text-foreground">{PORTFOLIO_TYPES.find((t) => t.value === formType)?.label}</span></div>
                    <div>Nome: <span className="text-foreground">{formName || '—'}</span></div>
                    <div>Budget: <span className="text-foreground">{formatCurrency(parseFloat(formBudget) || 0)}</span></div>
                    <div>Asset: <span className="text-foreground">{(formAssets?.length ? formAssets.join(', ') : '—')}</span></div>
                    <div>Strategia: <span className="text-foreground">{formStrategy || '—'}</span></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <DialogFooter className="gap-2">
            {wizardStep > 1 ? (
              <Button type="button" variant="outline" onClick={handleBack} disabled={submitting}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Indietro
              </Button>
            ) : (
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
                Annulla
              </Button>
            )}
            {wizardStep < 3 ? (
              <Button type="button" onClick={handleNext} data-testid="portfolio-wizard-next">
                Avanti
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button type="button" onClick={handleSubmit} disabled={submitting} data-testid="portfolio-form-submit">
                {submitting ? 'Creazione…' : 'Crea portafoglio'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfoliosPage;
